// Update Progress Modal - نافذة تقدم التحديث
// تعرض تقدم عملية التحديث بشكل تفاعلي

import { useEffect, useState } from 'react';
import { FiX, FiCheckCircle, FiXCircle, FiDownload, FiShield, FiArchive, FiPackage, FiPause, FiDatabase, FiUploadCloud, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { useLanguageStore } from '../../store/languageStore';
import { UpdateProgress } from '../../services/autoUpdateService';

interface UpdateProgressModalProps {
  isOpen: boolean;
  progress: UpdateProgress | null;
  onClose?: () => void;
}

export default function UpdateProgressModal({ isOpen, progress, onClose }: UpdateProgressModalProps) {
  const { language } = useLanguageStore();
  const isRTL = language === 'ar';

  if (!isOpen) return null;

  const getStageIcon = (stage: string) => {
    switch (stage.toLowerCase()) {
      case 'downloading':
        return <FiDownload className="w-6 h-6 animate-bounce text-blue-500" />;
      case 'verifying':
        return <FiShield className="w-6 h-6 text-green-500" />;
      case 'backup':
        return <FiArchive className="w-6 h-6 text-purple-500" />;
      case 'extracting':
        return <FiPackage className="w-6 h-6 text-orange-500" />;
      case 'stoppingservice':
        return <FiPause className="w-6 h-6 text-yellow-500" />;
      case 'databasemigration':
        return <FiDatabase className="w-6 h-6 text-indigo-500" />;
      case 'deploying':
        return <FiUploadCloud className="w-6 h-6 text-blue-500" />;
      case 'startingservice':
        return <FiPlay className="w-6 h-6 text-green-500" />;
      case 'healthcheck':
        return <FiCheckCircle className="w-6 h-6 text-green-500" />;
      case 'complete':
        return <FiCheckCircle className="w-6 h-6 text-green-500" />;
      case 'failed':
        return <FiXCircle className="w-6 h-6 text-red-500" />;
      default:
        return <FiRefreshCw className="w-6 h-6 animate-spin text-blue-500" />;
    }
  };

  const getStageName = (stage: string) => {
    const names: Record<string, { ar: string; en: string }> = {
      'Starting': { ar: 'بدء العملية', en: 'Starting' },
      'Downloading': { ar: 'تحميل التحديث', en: 'Downloading Update' },
      'Verifying': { ar: 'التحقق من السلامة', en: 'Verifying Integrity' },
      'Backup': { ar: 'إنشاء نسخة احتياطية', en: 'Creating Backup' },
      'Extracting': { ar: 'استخراج الملفات', en: 'Extracting Files' },
      'StoppingService': { ar: 'إيقاف الخدمة', en: 'Stopping Service' },
      'DatabaseMigration': { ar: 'تحديث قاعدة البيانات', en: 'Database Migration' },
      'Deploying': { ar: 'نشر التحديث', en: 'Deploying Update' },
      'StartingService': { ar: 'تشغيل الخدمة', en: 'Starting Service' },
      'HealthCheck': { ar: 'التحقق من الصحة', en: 'Health Check' },
      'Complete': { ar: 'اكتمل التحديث', en: 'Update Complete' },
      'Failed': { ar: 'فشل التحديث', en: 'Update Failed' },
    };

    return names[stage]?.[language] || stage;
  };

  const getProgressColor = () => {
    if (progress?.hasError) return 'bg-red-500';
    if (progress?.isComplete) return 'bg-green-500';
    return 'bg-blue-500';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {language === 'ar' ? 'تقدم التحديث' : 'Update Progress'}
          </h3>
          {onClose && !progress?.isComplete && !progress?.hasError && (
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <FiX className="w-6 h-6" />
            </button>
          )}
        </div>

        {/* Progress Content */}
        <div className="p-6">
          {progress ? (
            <>
              {/* Stage Icon and Name */}
              <div className="flex items-center gap-4 mb-6">
                {getStageIcon(progress.stage)}
                <div className="flex-1">
                  <h4 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {getStageName(progress.stage)}
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {progress.message}
                  </p>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-6">
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 mb-2">
                  <div
                    className={`h-3 rounded-full transition-all duration-300 ${getProgressColor()}`}
                    style={{ width: `${Math.max(progress.percentage, 5)}%` }}
                  />
                </div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                  <span>{progress.percentage.toFixed(0)}%</span>
                  <span>
                    {progress.isComplete
                      ? language === 'ar' ? 'مكتمل' : 'Complete'
                      : progress.hasError
                      ? language === 'ar' ? 'فشل' : 'Failed'
                      : language === 'ar' ? 'قيد التنفيذ' : 'In Progress'}
                  </span>
                </div>
              </div>

              {/* Error Message */}
              {progress.hasError && progress.errorMessage && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <FiXCircle className="w-5 h-5 text-red-600 dark:text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-red-900 dark:text-red-100 mb-1">
                        {language === 'ar' ? 'خطأ في التحديث' : 'Update Error'}
                      </p>
                      <p className="text-sm text-red-800 dark:text-red-200">
                        {progress.errorMessage}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Success Message */}
              {progress.isComplete && !progress.hasError && (
                <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-2">
                    <FiCheckCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-semibold text-green-900 dark:text-green-100 mb-1">
                        {language === 'ar' ? 'تم التحديث بنجاح' : 'Update Successful'}
                      </p>
                      <p className="text-sm text-green-800 dark:text-green-200">
                        {language === 'ar'
                          ? 'سيتم إعادة تشغيل التطبيق تلقائياً...'
                          : 'The application will restart automatically...'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {(progress.isComplete || progress.hasError) && onClose && (
                <div className="flex gap-2">
                  <button
                    onClick={onClose}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
                  >
                    {language === 'ar' ? 'إغلاق' : 'Close'}
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-8">
              <FiRefreshCw className="w-12 h-12 text-gray-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">
                {language === 'ar' ? 'جارٍ التحقق من التحديثات...' : 'Checking for updates...'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


